/**
 * OfflineDraftsPill — small "📥 N drafts pending" pill shown in the
 * page header whenever the SW outbox has at least one queued request
 * waiting to flush. Tap to open a drawer that lists the pending
 * destinations so the operator knows which wizard work is held up.
 *
 * iter150AZ.66 — Phase 1 wiring for offline-first Commissioning /
 * PM-service / Warranty wizards. Per the user's request, the pill is
 * compact and unobtrusive; the heavier "Drafts" list / manual-sync UX
 * lives behind a tap.
 *
 * Why a separate component instead of a hook in HeaderBrand?
 *   • HeaderBrand renders on most but not all pages (and the wizards
 *     that REALLY need this — Commissioning, ServiceTech — sometimes
 *     don't render it). Each top-level wizard imports + renders the
 *     pill directly so the affordance is visible even when the
 *     standard chrome isn't.
 *   • Keeps the offline UX testable in isolation.
 */
import { useState } from "react";
import { Tray, X, ArrowsClockwise } from "@phosphor-icons/react";
import useOfflineQueue from "@/lib/useOfflineQueue";
import { useT } from "@/lib/i18n";

export default function OfflineDraftsPill() {
  const t = useT();
  const { depth, online } = useOfflineQueue();
  const [open, setOpen] = useState(false);

  if (!depth) return null;

  const flush = () => {
    try {
      navigator.serviceWorker?.controller?.postMessage({ type: "FLUSH_OUTBOX" });
    } catch (_) { /* ignore */ }
  };

  return (
    <>
      <button
        type="button"
        data-testid="offline-drafts-pill"
        data-depth={depth}
        onClick={() => setOpen(true)}
        title={t("offline.drafts_pill_tip",
                  "Tap to see queued offline work waiting to sync")}
        className="inline-flex items-center gap-1 h-7 px-2 rounded-sm border text-[10px] font-bold uppercase tracking-wider bg-amber-50 text-amber-900 border-amber-300 hover:bg-amber-100 transition-colors shrink-0"
      >
        <Tray size={12} weight="bold" />
        <span>
          {t("offline.drafts_count", "{n} pending").replace("{n}", String(depth))}
        </span>
      </button>

      {open && (
        <div
          data-testid="offline-drafts-drawer"
          className="fixed inset-0 z-[100] bg-slate-900/30 flex items-end sm:items-center sm:justify-center"
          onClick={() => setOpen(false)}
        >
          <div
            className="bg-white w-full sm:max-w-md sm:rounded-sm shadow-xl border-t sm:border border-slate-200"
            onClick={(e) => e.stopPropagation()}
          >
            <header className="flex items-center gap-2 px-4 py-3 border-b border-slate-200">
              <Tray size={16} weight="fill" className="text-amber-700" />
              <h2 className="text-sm font-bold font-['Chivo']">
                {t("offline.drafts_title", "Pending offline work")}
              </h2>
              <span data-testid="offline-drafts-count" className="text-[10px] font-mono text-slate-500">
                {depth}
              </span>
              <button
                data-testid="offline-drafts-close"
                onClick={() => setOpen(false)}
                className="ml-auto w-7 h-7 inline-flex items-center justify-center rounded-sm hover:bg-slate-100 text-slate-500"
              >
                <X size={14} weight="bold" />
              </button>
            </header>
            <div className="px-4 py-3 space-y-3 text-sm text-slate-700 leading-relaxed">
              <p>
                {t("offline.drafts_body",
                    "Photos, videos and form submissions you captured while offline are queued on this device. They send automatically as soon as a signal returns.")}
              </p>
              <p className="text-[12px] text-slate-500">
                {online
                  ? t("offline.drafts_body_online",
                      "You're back online — the queue is flushing now. This pill will disappear when it's empty.")
                  : t("offline.drafts_body_offline",
                      "You're currently offline. Keep working — everything you do is being saved on the device.")}
              </p>
              <button
                type="button"
                data-testid="offline-drafts-flush"
                onClick={flush}
                disabled={!online}
                className="inline-flex items-center gap-1.5 h-9 px-3 text-xs font-bold rounded-sm border border-blue-200 bg-blue-50 text-blue-700 hover:bg-blue-100 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <ArrowsClockwise size={12} weight="bold" />
                {t("offline.drafts_flush_now", "Try to send now")}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
